import React, { useState, useEffect } from 'react';
import api from '../services/api';
import Button from '../components/Button';
import Card from '../components/Card';
import Modal from '../components/Modal';
import TextInput from '../components/TextInput';
import SearchInput from '../components/SearchInput';
import EmptyState, { EmptyStateIcons } from '../components/EmptyState';
import ErrorMessage from '../components/ErrorMessage';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import ItemDetails from '../components/ItemDetails';
import { logger } from '../utils/logger';

interface FamilyGroupOption {
  _id: string;
  name: string;
}

interface Item {
  _id: string;
  name: string;
  description: string;
  quantity: number;
  category: string;
  location: string;
  condition: 'new' | 'excellent' | 'good' | 'fair' | 'poor';
  status: 'available' | 'borrowed' | 'maintenance' | 'lost' | 'damaged';
  statusDetails: {
    updatedAt: string;
    updatedBy: {
      id: string;
      firstName: string;
      lastName: string;
    };
    notes?: string;
  };
  isAvailable: boolean;
  owner: {
    id: string;
    firstName: string;
    lastName: string;
  };
  familyGroup: string;
  createdAt: string;
  updatedAt: string;
  images?: Array<{ url: string; caption?: string }>;
  newImages?: File[];
  borrowedBy?: {
    user: {
      id: string;
      firstName: string;
      lastName: string;
    };
    borrowDate: string;
    expectedReturnDate: string;
    notes?: string;
  };
}

interface Category {
  _id: string;
  name: string;
  description?: string;
  icon?: string;
}

const Items: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [familyGroups, setFamilyGroups] = useState<FamilyGroupOption[]>([]);
  const [selectedFamilyGroupId, setSelectedFamilyGroupId] = useState<string>('');
  const [items, setItems] = useState<Item[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<Item | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [newItem, setNewItem] = useState({
    name: '',
    description: '',
    category: '',
    location: '',
    condition: 'good' as const,
    familyGroupId: '', // This should be set to the user's current family group
    images: [] as File[]
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEditSubmitting, setIsEditSubmitting] = useState(false);

  useEffect(() => {
    if (user?.familyGroups && user.familyGroups.length > 0) {
      fetchFamilyGroups();
    }
  }, [user]);

  useEffect(() => {
    if (selectedFamilyGroupId) {
      fetchItems();
      fetchCategories();
      // Store the selected group ID in localStorage
      localStorage.setItem('selectedFamilyGroupId', selectedFamilyGroupId);
    }
  }, [selectedFamilyGroupId]);

  const fetchFamilyGroups = async () => {
    try {
      const response = await api.get('/family-groups');
      setFamilyGroups(response.data);

      // Get the stored family group ID from localStorage
      const storedGroupId = localStorage.getItem('selectedFamilyGroupId');
      
      // Check if the stored ID exists in the user's family groups
      const isValidStoredGroup = response.data.some((group: FamilyGroupOption) => group._id === storedGroupId);
      
      // Set the selected group ID (either stored or first available)
      if (storedGroupId && isValidStoredGroup) {
        setSelectedFamilyGroupId(storedGroupId);
      } else if (response.data.length > 0) {
        setSelectedFamilyGroupId(response.data[0]._id);
      }
    } catch (error) {
      logger.error('Error fetching family groups:', error);
      setError('Failed to load family groups. Please try again.');
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await api.get(`/family-groups/${selectedFamilyGroupId}/categories`);
      setCategories(response.data);
    } catch (error) {
      logger.error('Error fetching categories:', error);
      setError('Failed to load categories. Please try again.');
    }
  };

  const fetchItems = async () => {
    try {
      setIsLoading(true);
      const response = await api.get(`/items/family/${selectedFamilyGroupId}`);
      logger.debug('Fetched items:', response.data);
      setItems(response.data);
      setError(null);
    } catch (error) {
      logger.error('Error fetching items:', error);
      setError('Failed to load items. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateItem = async () => {
    if (isSubmitting) return;
    
    try {
      setIsSubmitting(true);
      setErrors({});

      const formData = new FormData();
      formData.append('name', newItem.name);
      formData.append('description', newItem.description);
      formData.append('category', newItem.category);
      formData.append('location', newItem.location);
      formData.append('condition', newItem.condition);
      formData.append('familyGroupId', newItem.familyGroupId || selectedFamilyGroupId);

      newItem.images.forEach((image) => {
        formData.append('images', image);
      });

      const response = await api.post('/items', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      // Only add to items list if it belongs to the currently selected group
      if (response.data.familyGroup === selectedFamilyGroupId) {
        setItems([...items, response.data]);
      }

      setIsAddModalOpen(false);

      // Reset the new item form
      setNewItem({
        name: '',
        description: '',
        category: '',
        location: '',
        condition: 'good' as const,
        familyGroupId: '',
        images: []
      });
    } catch (error) {
      logger.error('Error creating item:', error);
      setErrors({ submit: 'Failed to create item. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleUpdateItem = async () => {
    if (!selectedItem || isEditSubmitting) return;

    try {
      setIsEditSubmitting(true);
      const formData = new FormData();
      formData.append('name', selectedItem.name);
      formData.append('description', selectedItem.description);
      formData.append('category', selectedItem.category);
      formData.append('location', selectedItem.location);
      formData.append('condition', selectedItem.condition);
      formData.append('status', selectedItem.status);
      formData.append('familyGroup', selectedItem.familyGroup);

      if (selectedItem.statusDetails?.notes) {
        formData.append('statusDetails', JSON.stringify({ notes: selectedItem.statusDetails.notes }));
      }

      if (selectedItem.borrowedBy) {
        formData.append('borrowedBy', JSON.stringify(selectedItem.borrowedBy));
      }

      if (selectedItem.images && selectedItem.images.length > 0) {
        logger.debug('Existing images:', selectedItem.images);
        formData.append('existingImages', JSON.stringify(selectedItem.images));
      }

      if (selectedItem.newImages && selectedItem.newImages.length > 0) {
        logger.debug('Adding new images:', selectedItem.newImages.length);
        selectedItem.newImages.forEach((image) => {
          formData.append('images', image);
        });
      }

      const response = await api.patch(`/items/${selectedItem._id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
      });

      logger.debug('Update response:', response.data);

      const updatedItem = {
        ...response.data,
        owner: typeof response.data.owner === 'string' ? {
          id: response.data.owner,
          firstName: selectedItem.owner.firstName,
          lastName: selectedItem.owner.lastName
        } : response.data.owner
      };

      // Batch all state updates together
      const updateStates = () => {
        if (updatedItem.familyGroup !== selectedFamilyGroupId) {
          setItems(prevItems => prevItems.filter(item => item._id !== selectedItem._id));
        } else {
          setItems(prevItems => prevItems.map(item =>
            item._id === selectedItem._id ? updatedItem : item
          ));
        }
        setSelectedItem(null);
        setErrors({});
        setIsDetailsModalOpen(false);
        setIsEditModalOpen(false);
      };

      updateStates();
    } catch (error) {
      logger.error('Error updating item:', error);
      setErrors({ submit: 'Failed to update item. Please try again.' });
    } finally {
      setIsEditSubmitting(false);
    }
  };

  const handleDeleteItem = async () => {
    if (!selectedItem) return;

    try {
      await api.delete(`/items/${selectedItem._id}`);
      setItems(items.filter(item => item._id !== selectedItem._id));
      setIsDeleteModalOpen(false);
      setSelectedItem(null);
    } catch (error) {
      logger.error('Error deleting item:', error);
    }
  };

  const getCategoryName = (categoryId: string): string => {
    const category = categories.find(cat => cat._id === categoryId);
    return category ? category.name : 'Uncategorized';
  };

  if (!user?.familyGroups?.length) {
    return (
      <div className="py-6">
        <div className="max-w-7xl mx-auto px-6">
          <Card>
            <EmptyState
              title="No Family Group"
              description="You need to be part of a family group to manage items. Create or join a family group to get started."
              icon={EmptyStateIcons.family}
              action={{
                label: 'Manage Family Groups',
                onClick: () => navigate('/groups')
              }}
            />
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="py-6">
      <div className="w-full px-6">
        <Card>
          <div className="px-6 py-5 border-b border-gray-200 sm:flex sm:justify-between sm:items-center flex-wrap">
            <div className="flex flex-col sm:flex-row items-start sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-4 sm:mb-0">
              <h1 className="text-2xl font-semibold text-gray-900">Items</h1>
              {familyGroups.length > 1 && (
                <div className="w-full sm:w-auto">
                  <select
                    className="block w-full rounded-md border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    value={selectedFamilyGroupId}
                    onChange={(e) => setSelectedFamilyGroupId(e.target.value)}
                  >
                    {familyGroups.map((group) => (
                      <option key={group._id} value={group._id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>
            <div className="w-full sm:w-auto">
              <Button onClick={() => setIsAddModalOpen(true)} className="w-full sm:w-auto justify-center">
                Add Item
              </Button>
            </div>
          </div>

          <div className="p-6">
            <div className="mb-6">
              <SearchInput
                onSearch={setSearchQuery}
                placeholder="Search items..."
              />
            </div>

            {error && (
              <ErrorMessage
                message={error}
                className="mb-6"
                onDismiss={() => setError(null)}
              />
            )}

            {isLoading ? (
              <div className="animate-pulse space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="bg-gray-100 p-4 rounded-lg">
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                    <div className="h-3 bg-gray-100 rounded w-1/4 mt-2"></div>
                  </div>
                ))}
              </div>
            ) : items.length === 0 ? (
              <EmptyState
                title="No items"
                description="Get started by adding your first item."
                icon={EmptyStateIcons.items}
                action={{
                  label: 'Add Item',
                  onClick: () => setIsAddModalOpen(true)
                }}
              />
            ) : (
              <div className="grid grid-cols-1 gap-4">
                {items
                  .filter(item =>
                    item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    item.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    item.category.toLowerCase().includes(searchQuery.toLowerCase()) ||
                    item.location.toLowerCase().includes(searchQuery.toLowerCase())
                  )
                  .map((item) => (
                    <div
                      key={item._id}
                      className="bg-white rounded-lg shadow overflow-hidden hover:shadow-md transition-shadow duration-200"
                    >
                      <div className="p-4 sm:p-6">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2">
                              <h3 className="text-lg font-medium text-gray-900 truncate">
                                {item.name}
                              </h3>
                              {item.images && item.images.length > 0 && (
                                <div className="flex items-center space-x-1">
                                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-blue-500">
                                    <path fillRule="evenodd" d="M1.5 6a2.25 2.25 0 012.25-2.25h16.5A2.25 2.25 0 0122.5 6v12a2.25 2.25 0 01-2.25 2.25H3.75A2.25 2.25 0 011.5 18V6zM3 16.06V18c0 .414.336.75.75.75h16.5A.75.75 0 0021 18v-1.94l-2.69-2.689a1.5 1.5 0 00-2.12 0l-.88.879.97.97a.75.75 0 11-1.06 1.06l-5.16-5.159a1.5 1.5 0 00-2.12 0L3 16.061zm10.125-7.81a1.125 1.125 0 112.25 0 1.125 1.125 0 01-2.25 0z" clipRule="evenodd" />
                                  </svg>
                                  <span className="text-sm text-blue-500 font-medium">{item.images.length}</span>
                                </div>
                              )}
                            </div>
                            <div className="mt-1 flex flex-col sm:flex-row sm:flex-wrap sm:space-x-4">
                              <div className="mt-2 flex items-center text-sm text-gray-500">
                                <svg className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                                  <path d="M11.47 3.84a.75.75 0 011.06 0l8.69 8.69a.75.75 0 101.06-1.06l-8.689-8.69a2.25 2.25 0 00-3.182 0l-8.69 8.69a.75.75 0 001.061 1.06l8.69-8.69z" />
                                  <path d="M12 5.432l8.159 8.159c.03.03.06.058.091.086v6.198c0 1.035-.84 1.875-1.875 1.875H15a.75.75 0 01-.75-.75v-4.5a.75.75 0 00-.75-.75h-3a.75.75 0 00-.75.75V21a.75.75 0 01-.75.75H5.625a1.875 1.875 0 01-1.875-1.875v-6.198a2.29 2.29 0 00.091-.086L12 5.43z" />
                                </svg>
                                {item.location}
                              </div>
                              <div className="mt-2 flex items-center text-sm text-gray-500">
                                <svg className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                                  <path fillRule="evenodd" d="M7.5 5.25a3 3 0 013-3h3a3 3 0 013 3v.205c.933.085 1.857.197 2.774.334 1.454.218 2.476 1.483 2.476 2.917v3.033c0 1.211-.734 2.352-1.936 2.752A24.726 24.726 0 0112 15.75c-2.73 0-5.357-.442-7.814-1.259-1.202-.4-1.936-1.541-1.936-2.752V8.706c0-1.434 1.022-2.7 2.476-2.917A48.814 48.814 0 017.5 5.455V5.25zm7.5 0v.09a49.488 49.488 0 00-6 0v-.09a1.5 1.5 0 011.5-1.5h3a1.5 1.5 0 011.5 1.5zm-3 8.25a.75.75 0 100-1.5.75.75 0 000 1.5z" clipRule="evenodd" />
                                </svg>
                                {getCategoryName(item.category)}
                              </div>
                              <div className="mt-2 flex items-center text-sm text-gray-500">
                                <svg className="mr-1.5 h-5 w-5 flex-shrink-0 text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                                  <path fillRule="evenodd" d="M7.5 6a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0zM3.751 20.105a8.25 8.25 0 0116.498 0 .75.75 0 01-.437.695A18.683 18.683 0 0112 22.5c-2.786 0-5.433-.608-7.812-1.7a.75.75 0 01-.437-.695z" clipRule="evenodd" />
                                </svg>
                                {`${item.owner.firstName} ${item.owner.lastName}`}
                              </div>
                            </div>
                            <p className="mt-2 text-sm text-gray-500 line-clamp-2">{item.description}</p>
                          </div>
                          <div className="ml-4 flex flex-col items-end space-y-2">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium capitalize
                                ${item.status === 'available' ? 'bg-green-100 text-green-800' :
                                  item.status === 'borrowed' ? 'bg-blue-100 text-blue-800' :
                                    item.status === 'maintenance' ? 'bg-yellow-100 text-yellow-800' :
                                      item.status === 'lost' ? 'bg-red-100 text-red-800' :
                                        'bg-gray-100 text-gray-800'
                                }`}
                            >
                              {item.status}
                            </span>
                            <div className="flex flex-col sm:flex-row gap-2">
                              <Button
                                variant="secondary"
                                size="small"
                                onClick={() => {
                                  setSelectedItem(item);
                                  setIsDetailsModalOpen(true);
                                }}
                              >
                                View
                              </Button>
                              <Button
                                variant="secondary"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedItem(item);
                                  setIsEditModalOpen(true);
                                }}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="danger"
                                size="small"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedItem(item);
                                  setIsDeleteModalOpen(true);
                                }}
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </Card>

        {/* Create Modal */}
        <Modal
          isOpen={isAddModalOpen}
          onClose={() => {
            if (!isSubmitting) {
              setIsAddModalOpen(false);
            }
          }}
          title="Add New Item"
          actions={{
            primary: {
              label: isSubmitting ? 'Creating...' : 'Create Item',
              onClick: handleCreateItem,
              disabled: isSubmitting
            },
            secondary: {
              label: 'Cancel',
              onClick: () => {
                if (!isSubmitting) {
                  setIsAddModalOpen(false);
                }
              },
              disabled: isSubmitting
            }
          }}
          afterLeave={() => {
            setNewItem({
              name: '',
              description: '',
              category: '',
              location: '',
              condition: 'good',
              familyGroupId: '',
              images: []
            });
            setErrors({});
            setIsSubmitting(false);
          }}
        >
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Name</label>
              <TextInput
                value={newItem.name}
                onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                placeholder="Enter item name"
                error={errors.name}
                label=""
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Description</label>
              <textarea
                value={newItem.description}
                onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                placeholder="Enter item description"
                rows={3}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Category</label>
              <select
                value={newItem.category}
                onChange={(e) => setNewItem({ ...newItem, category: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="">Select a category</option>
                {categories.map((category) => (
                  <option key={category._id} value={category._id}>
                    {category.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Family Group</label>
              <select
                value={newItem.familyGroupId || selectedFamilyGroupId}
                onChange={(e) => setNewItem({ ...newItem, familyGroupId: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                {familyGroups.map((group) => (
                  <option key={group._id} value={group._id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Location</label>
              <TextInput
                value={newItem.location}
                onChange={(e) => setNewItem({ ...newItem, location: e.target.value })}
                placeholder="Enter item location"
                error={errors.location}
                label=""
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Condition</label>
              <select
                value={newItem.condition}
                onChange={(e) => setNewItem({ ...newItem, condition: e.target.value as any })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              >
                <option value="new">New</option>
                <option value="excellent">Excellent</option>
                <option value="good">Good</option>
                <option value="fair">Fair</option>
                <option value="poor">Poor</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Images</label>
              <input
                type="file"
                id="images"
                multiple
                accept="image/*"
                onChange={(e) => {
                  if (isSubmitting) return;
                  const files = Array.from(e.target.files || []);
                  // Validate file size and type
                  const validFiles = files.filter(file => {
                    const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB limit
                    const isValidType = file.type.startsWith('image/');
                    if (!isValidSize) {
                      setErrors(prev => ({ ...prev, images: 'Image size must be less than 5MB' }));
                    }
                    if (!isValidType) {
                      setErrors(prev => ({ ...prev, images: 'Invalid file type. Please upload images only.' }));
                    }
                    return isValidSize && isValidType;
                  });
                  setNewItem({ ...newItem, images: validFiles });
                  // Clear the camera capture input
                  const cameraCaptureInput = document.getElementById('camera-capture') as HTMLInputElement;
                  if (cameraCaptureInput) {
                    cameraCaptureInput.value = '';
                  }
                }}
                className="mt-1 block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-md file:border-0
                  file:text-sm file:font-semibold
                  file:bg-indigo-50 file:text-indigo-700
                  hover:file:bg-indigo-100"
                disabled={isSubmitting}
              />
              <input
                type="file"
                id="camera-capture"
                accept="image/*"
                capture="environment"
                className="hidden"
                onChange={(e) => {
                  if (isSubmitting) return;
                  const files = Array.from(e.target.files || []);
                  if (files.length > 0) {
                    // Validate file size and type
                    const validFiles = files.filter(file => {
                      const isValidSize = file.size <= 5 * 1024 * 1024; // 5MB limit
                      const isValidType = file.type.startsWith('image/');
                      if (!isValidSize) {
                        setErrors(prev => ({ ...prev, images: 'Image size must be less than 5MB' }));
                      }
                      if (!isValidType) {
                        setErrors(prev => ({ ...prev, images: 'Invalid file type. Please upload images only.' }));
                      }
                      return isValidSize && isValidType;
                    });
                    setNewItem({ ...newItem, images: validFiles });
                    // Clear the regular file input
                    const fileInput = document.getElementById('images') as HTMLInputElement;
                    if (fileInput) {
                      fileInput.value = '';
                    }
                  }
                }}
                disabled={isSubmitting}
              />
              <button
                type="button"
                onClick={() => {
                  if (isSubmitting) return;
                  const input = document.getElementById('camera-capture') as HTMLInputElement;
                  if (input) {
                    input.value = ''; // Clear any existing files
                    input.click();
                  }
                }}
                className={`mt-2 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 ${
                  isSubmitting 
                    ? 'bg-gray-100 cursor-not-allowed' 
                    : 'bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'
                }`}
                disabled={isSubmitting}
              >
                <svg className="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                </svg>
                {isSubmitting ? 'Processing...' : 'Take Photo'}
              </button>
              {errors.images && (
                <p className="mt-2 text-sm text-red-600">{errors.images}</p>
              )}
            </div>
          </div>
        </Modal>

        {/* Edit Modal */}
        <Modal
          isOpen={isEditModalOpen}
          onClose={() => {
            if (!isEditSubmitting) {
              setIsEditModalOpen(false);
              // Don't reset other states here, do it in afterLeave
            }
          }}
          title="Edit Item"
          actions={{
            primary: {
              label: isEditSubmitting ? 'Saving...' : 'Save Changes',
              onClick: handleUpdateItem,
              disabled: isEditSubmitting
            },
            secondary: {
              label: 'Cancel',
              onClick: () => {
                if (!isEditSubmitting) {
                  setIsEditModalOpen(false);
                  // Don't reset other states here, do it in afterLeave
                }
              },
              disabled: isEditSubmitting
            }
          }}
          afterLeave={() => {
            // Reset all modal-related states after the animation completes
            setSelectedItem(null);
            setErrors({});
            setIsDetailsModalOpen(false);
            setIsEditSubmitting(false);
          }}
        >
          {selectedItem && (
            <div className="relative">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Name</label>
                  <TextInput
                    value={selectedItem.name}
                    onChange={(e) => setSelectedItem({ ...selectedItem, name: e.target.value })}
                    placeholder="Enter item name"
                    label=""
                    disabled={isEditSubmitting}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    value={selectedItem.description}
                    onChange={(e) => setSelectedItem({ ...selectedItem, description: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    placeholder="Enter item description"
                    rows={3}
                    disabled={isEditSubmitting}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Category</label>
                  <select
                    value={selectedItem.category}
                    onChange={(e) => setSelectedItem({ ...selectedItem, category: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    disabled={isEditSubmitting}
                  >
                    <option value="">Select a category</option>
                    {categories.map((category) => (
                      <option key={category._id} value={category._id}>
                        {category.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Family Group</label>
                  <select
                    value={selectedItem.familyGroup}
                    onChange={(e) => setSelectedItem({ ...selectedItem, familyGroup: e.target.value })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    disabled={isEditSubmitting}
                  >
                    {familyGroups.map((group) => (
                      <option key={group._id} value={group._id}>
                        {group.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Location</label>
                  <TextInput
                    value={selectedItem.location}
                    onChange={(e) => setSelectedItem({ ...selectedItem, location: e.target.value })}
                    placeholder="Enter item location"
                    label=""
                    disabled={isEditSubmitting}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Condition</label>
                  <select
                    value={selectedItem.condition}
                    onChange={(e) => setSelectedItem({ ...selectedItem, condition: e.target.value as any })}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    disabled={isEditSubmitting}
                  >
                    <option value="new">New</option>
                    <option value="excellent">Excellent</option>
                    <option value="good">Good</option>
                    <option value="fair">Fair</option>
                    <option value="poor">Poor</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Images</label>
                  {selectedItem.images && selectedItem.images.length > 0 && (
                    <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-4">
                      {selectedItem.images.map((image, index) => (
                        <div key={index} className="relative group">
                          <img
                            src={image.url}
                            alt={image.caption || `Image ${index + 1}`}
                            className="h-24 w-24 object-cover rounded-lg"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedItem({
                                ...selectedItem,
                                images: selectedItem.images?.filter((_, i) => i !== index)
                              });
                            }}
                            className="absolute top-1 right-1 p-1 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            disabled={isEditSubmitting}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                            </svg>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  <input
                    type="file"
                    id="edit-images"
                    multiple
                    accept="image/*"
                    onChange={(e) => {
                      if (!isEditSubmitting) {
                        const files = Array.from(e.target.files || []);
                        setSelectedItem({ ...selectedItem, newImages: files });
                        // Clear the camera capture input
                        const cameraCaptureInput = document.getElementById('edit-camera-capture') as HTMLInputElement;
                        if (cameraCaptureInput) {
                          cameraCaptureInput.value = '';
                        }
                      }
                    }}
                    className="mt-1 block w-full text-sm text-gray-500
                      file:mr-4 file:py-2 file:px-4
                      file:rounded-md file:border-0
                      file:text-sm file:font-semibold
                      file:bg-indigo-50 file:text-indigo-700
                      hover:file:bg-indigo-100
                      disabled:opacity-50 disabled:cursor-not-allowed"
                    disabled={isEditSubmitting}
                  />
                  <input
                    type="file"
                    id="edit-camera-capture"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={(e) => {
                      if (!isEditSubmitting) {
                        const files = Array.from(e.target.files || []);
                        if (files.length > 0) {
                          setSelectedItem({ ...selectedItem, newImages: files });
                          // Clear the regular file input
                          const fileInput = document.getElementById('edit-images') as HTMLInputElement;
                          if (fileInput) {
                            fileInput.value = '';
                          }
                        }
                      }
                    }}
                    disabled={isEditSubmitting}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      if (!isEditSubmitting) {
                        const input = document.getElementById('edit-camera-capture') as HTMLInputElement;
                        if (input) {
                          input.value = ''; // Clear any existing files
                          input.click();
                        }
                      }
                    }}
                    className={`mt-2 inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm leading-4 font-medium rounded-md text-gray-700 
                      ${isEditSubmitting 
                        ? 'bg-gray-100 cursor-not-allowed opacity-50' 
                        : 'bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500'}`}
                    disabled={isEditSubmitting}
                  >
                    <svg className="-ml-0.5 mr-2 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M4 5a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V7a2 2 0 00-2-2h-1.586a1 1 0 01-.707-.293l-1.121-1.121A2 2 0 0011.172 3H8.828a2 2 0 00-1.414.586L6.293 4.707A1 1 0 015.586 5H4zm6 9a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
                    </svg>
                    Take Photo
                  </button>
                </div>
              </div>

              {/* Loading Overlay */}
              {isEditSubmitting && (
                <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center z-10">
                  <div className="flex items-center space-x-2 bg-white px-4 py-2 rounded-lg shadow-sm">
                    <svg className="animate-spin h-5 w-5 text-indigo-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm font-medium text-gray-900">Saving changes...</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </Modal>

        {/* Delete Modal */}
        {isDeleteModalOpen && selectedItem && (
          <Modal
            isOpen={isDeleteModalOpen}
            title="Delete Item"
            onClose={() => {
              setIsDeleteModalOpen(false);
              setSelectedItem(null);
            }}
          >
            <div className="p-6">
              <p className="mb-4">Are you sure you want to delete this item?</p>
              <div className="flex justify-end space-x-4">
                <Button
                  variant="secondary"
                  onClick={() => setIsDeleteModalOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="danger"
                  onClick={handleDeleteItem}
                >
                  Delete
                </Button>
              </div>
            </div>
          </Modal>
        )}

        {/* Details Modal */}
        {isDetailsModalOpen && selectedItem && (
          <ItemDetails
            item={selectedItem}
            categories={categories}
            isOpen={isDetailsModalOpen}
            onClose={() => setIsDetailsModalOpen(false)}
          />
        )}
      </div>
    </div>
  );
};

export default Items; 